import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import moduleRealtimeService from '../../../services/moduleRealtimeService';

const QAQCSummary = ({ data, onQAQCUpdate }) => {
  const { projectId } = useParams();

  // Real-time subscription for QA/QC changes
  React.useEffect(() => {
    if (!projectId) return;

    const qaqcChannel = moduleRealtimeService?.subscribeToQAQCChanges(
      projectId,
      (payload) => {
        console.log('QA/QC change detected:', payload?.event);
        
        // Trigger QA/QC refresh when data changes
        if (onQAQCUpdate) {
          onQAQCUpdate();
        }
      }
    );

    // Cleanup subscription on unmount
    return () => {
      moduleRealtimeService?.unsubscribe(qaqcChannel);
    };
  }, [projectId, onQAQCUpdate]);

  if (!data) {
    return (
      <div className="bg-card rounded-xl border border-border p-3 min-w-0">
        <h2 className="text-sm font-semibold text-foreground mb-3 truncate">QA/QC Overview</h2>
        <p className="text-muted-foreground text-xs">No QA/QC data available</p>
      </div>
    );
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const passRate = data?.totalInspections > 0 
    ? (data?.passedInspections / data?.totalInspections) * 100 
    : 0;

  const metrics = [
    { label: 'Total Inspections', value: data?.totalInspections, icon: 'ClipboardCheck', color: 'var(--color-primary)' },
    { label: 'Passed', value: data?.passed, icon: 'CheckCircle', color: 'var(--color-success)' },
    { label: 'Failed', value: data?.failed, icon: 'XCircle', color: 'var(--color-error)' },
    { label: 'Pending', value: data?.pending, icon: 'Clock', color: 'var(--color-warning)' }
  ];

  return (
    <div className="bg-card rounded-xl border border-border p-3 h-full min-w-0">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-sm font-semibold text-foreground truncate">QA/QC Overview</h2>
        <Icon name="Shield" size={16} color="var(--color-primary)" />
      </div>

      <div className="space-y-2">
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs text-muted-foreground">Total Inspections</span>
            <span className="text-lg font-bold text-foreground">{data?.totalInspections}</span>
          </div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-muted-foreground">Passed</span>
            <span className="text-sm font-semibold text-success">{data?.passedInspections}</span>
          </div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-muted-foreground">Failed</span>
            <span className="text-sm font-semibold text-error">{data?.failedInspections}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">Pending</span>
            <span className="text-sm font-semibold text-warning">{data?.pendingInspections}</span>
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs text-muted-foreground">Pass Rate</span>
            <span className="text-xs font-medium text-foreground">{passRate?.toFixed(1)}%</span>
          </div>
          <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{
                width: `${passRate}%`,
                backgroundColor: passRate >= 95 ? 'var(--color-success)' : passRate >= 80 ? 'var(--color-warning)' : 'var(--color-error)'
              }}
            />
          </div>
        </div>

        <div className="border-t border-border pt-3">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs text-muted-foreground">Defects Found</span>
            <span className="text-sm font-semibold text-foreground">{data?.defectsFound}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">Defects Resolved</span>
            <span className="text-sm font-semibold text-success">{data?.defectsResolved}</span>
          </div>
        </div>

        {data?.criticalIssues > 0 && (
          <div className="p-2 bg-error/10 rounded-lg border border-error/20">
            <div className="flex items-center gap-2">
              <Icon name="AlertTriangle" size={14} color="var(--color-error)" />
              <span className="text-xs font-medium text-error">
                {data?.criticalIssues} Critical Issue{data?.criticalIssues > 1 ? 's' : ''}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default QAQCSummary;